/* eslint-disable react-hooks/exhaustive-deps */
import { Fab, Typography } from "@mui/material";
import React, { useEffect, useMemo } from "react";
import { AiOutlineDelete } from "react-icons/ai";
import { GoPlus } from "react-icons/go";
import { GrClear } from "react-icons/gr";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import CompletableTask from "../../../../components/CompletableTask";
import Loader from "../../../../components/Loader";
import {
  addCompletedTaskToSync,
  setTasksLastSyncDate,
} from "../../../../config/redux/slices/schedulerSlice";
import {
  selectDailyTasksData,
  selectDailyTasksToSync,
  selectTasksLastSyncDate,
} from "../../../../config/redux/slices/schedulerSlice/selectors";
import { fetchAllDailyTasks } from "../../../../config/redux/slices/schedulerSlice/thunks";
import { formatDateToString } from "../../../../utils/date";
import TaskAdditionModal from "../AdditionModal";
import ClearSyncDialog from "../ClearSyncDialog";
import TaskDeletionModal from "../DeletionModal";
import TaskDetailsModal from "../TaskDetailsModal";
import {
  StyledBottomCta,
  StyledHeader,
  StyledSchedulerDashboard,
  StyledTasks,
} from "./styles";
import { getTasksListForUI } from "./utils";

const SchedulerDashboard = () => {
  const dispatch = useDispatch();
  const tasksData = useSelector(selectDailyTasksData);
  const tasksToSync = useSelector(selectDailyTasksToSync);
  const lastSyncDate = useSelector(selectTasksLastSyncDate);
  const [addModalVisible, setAddModalVisible] = React.useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = React.useState(false);
  const [clearSyncDialogVisible, setClearSyncDialogVisible] =
    React.useState(false);
  const tasksList = useMemo(
    () => getTasksListForUI(tasksData.data, tasksToSync),
    [tasksData, tasksToSync]
  );
  const isLoading = tasksData.state === "pending";

  useEffect(() => {
    const currentDate = formatDateToString(new Date());
    if (
      ["initial", "failed"].includes(tasksData.state) ||
      lastSyncDate !== currentDate
    ) {
      dispatch(fetchAllDailyTasks());
      dispatch(setTasksLastSyncDate(currentDate));
    }
  }, []);

  const onTaskComplete = (task) => {
    toast.success(`Completed: ${task.title}. Sync to upload`);
    dispatch(addCompletedTaskToSync(task.id));
  };

  const onAddClick = () => setAddModalVisible(true);
  const onDeleteClick = () => setDeleteModalVisible(true);
  const onClearSyncClick = () => setClearSyncDialogVisible(true);

  return (
    <StyledSchedulerDashboard>
      <StyledBottomCta>
        <Fab color="primary" onClick={onAddClick}>
          <GoPlus size="24px" color="white" />
        </Fab>
        {!!tasksList.length && (
          <Fab color="error" onClick={onDeleteClick}>
            <AiOutlineDelete size="24px" color="white" />
          </Fab>
        )}
        {!!tasksToSync.length && (
          <Fab color="warning" onClick={onClearSyncClick}>
            <GrClear size="24px" color="white" />
          </Fab>
        )}
      </StyledBottomCta>
      <TaskAdditionModal
        visible={addModalVisible}
        setVisible={setAddModalVisible}
      />
      <TaskDeletionModal
        visible={deleteModalVisible}
        setVisible={setDeleteModalVisible}
      />
      <ClearSyncDialog
        visible={clearSyncDialogVisible}
        setVisible={setClearSyncDialogVisible}
      />
      <StyledHeader>
        <Typography variant="h6">Today's Tasks ({tasksList.length})</Typography>
      </StyledHeader>
      {isLoading ? (
        <Loader hideBg={false} showText="Loading tasks" />
      ) : (
        <StyledTasks>
          {tasksList.map((item) => {
            return (
              <CompletableTask
                key={item.key}
                variant="circle"
                icon={item.emoji}
                title={item.title}
                popupText={item.popupText}
                data={item.data}
                completed={item.completed}
                onTarget={() => onTaskComplete(item)}
                renderModal={(data, setShowModal) => (
                  <TaskDetailsModal data={data} setShowModal={setShowModal} />
                )}
              />
            );
          })}
        </StyledTasks>
      )}
    </StyledSchedulerDashboard>
  );
};

export default SchedulerDashboard;
